/**
 * BatchAUIGrid.jsx - AUIGrid 기반 배치 검증 그리드
 * 
 * Google AI Studio 스타일 + AUIGrid 기능:
 * - 가로/세로 스크롤
 * - 컬럼 헤더 더블클릭 → 자동 너비 조절
 * - 드래그로 컬럼 폭 조절
 * - 셀 클릭 → bbox 하이라이트 연동
 * - 내장 Excel 내보내기
 */
import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';

// ============================================================
// 📐 계층적 컬럼 너비 설정 시스템
// ============================================================

/**
 * 1단계: 기본 데이터타입 너비 (모든 문서 공통)
 */
const BASE_WIDTH = {
    // 숫자 타입
    numeric: 80,      // 일반 숫자
    currency: 95,     // 금액
    quantity: 70,     // 수량
    percentage: 60,   // 퍼센트
    index: 45,        // 순번/인덱스
    // 텍스트 타입
    code: 90,         // 코드 (Material, HS Code 등)
    shortText: 100,   // 짧은 텍스트
    mediumText: 150,  // 중간 텍스트
    longText: 200,    // 긴 텍스트 (Description 등)
    date: 90,         // 날짜
    unit: 55,         // 단위 (EA, KG 등)
};

/**
 * 2단계: 문서 유형별 특화 필드 너비
 */
const DOCUMENT_TYPE_WIDTHS = {
    // 🚢 수출입 선적서류 (Invoice, Packing List, B/L, L/C 등) - 95% 사용
    shipping: {
        // 인보이스 필드
        item: 45, no: 45, seq: 45,
        invoiceno: 120, 'invoice no': 120, 'invoice number': 120,
        blno: 120, 'bl no': 120, 'b/l no': 120,
        pono: 110, 'po no': 110, 'po number': 110, 'purchase order': 120,
        // 품목 필드
        description: 220, desc: 220, 품명: 220, 'goods description': 220, 'description of goods': 250,
        material: 95, 'material no': 100, materialcode: 100,
        hscode: 85, 'hs code': 85, hsn: 85, hts: 85, tariff: 85,
        origin: 70, 'country of origin': 100,
        // 수량/금액 필드
        quantity: 70, qty: 70, '수량': 70,
        unit: 55, uom: 55, 'unit of measure': 70,
        unitprice: 90, 'unit price': 90, 단가: 90, price: 85,
        amount: 100, total: 100, 금액: 100, 'total amount': 110,
        currency: 60, curr: 55,
        // 중량/용적 필드
        grossweight: 85, 'gross weight': 90, netweight: 85, 'net weight': 90, weight: 80,
        cbm: 70, m3: 70, volume: 80,
        packages: 70, cartons: 70, ctns: 60, pcs: 55,
    },

    // 🏢 사업자등록증
    businessRegistration: {
        사업자등록번호: 130, 사업자번호: 130,
        상호: 150, 법인명: 150, 회사명: 150,
        대표자: 100, 대표자명: 100, 성명: 80,
        업태: 120, 종목: 120, 업종: 100,
        사업장주소: 250, 주소: 200, 본점소재지: 250,
        법인등록번호: 130, 개업일: 90, 개업연월일: 100,
    },

    // 📄 전자세금계산서
    taxInvoice: {
        전자세금계산서번호: 150, 승인번호: 140,
        공급자사업자번호: 130, 공급받는자사업자번호: 150,
        공급자상호: 150, 공급받는자상호: 150,
        작성일: 90, 공급일: 90, 발행일: 90,
        공급가액: 110, 세액: 90, 합계금액: 110,
        품목: 180, 규격: 100, 단가: 85, 수량: 70, 공급가: 100,
        비고: 150,
    },

    // 🚗 말소증/차량등록
    vehicleRegistration: {
        자동차등록번호: 130, 차량번호: 120,
        차명: 120, 차종: 100,
        연식: 70, 최초등록일: 100, 말소일: 90,
        차대번호: 180, vin: 180,
        원동기형식: 120, 배기량: 80,
        사용본거지: 200, 소유자: 100,
    }
};

/**
 * 3단계: 필드 키워드 → 데이터타입 매핑 (기본 너비 fallback)
 */
const FIELD_TYPE_PATTERNS = [
    // 인덱스/순번
    { pattern: /^(item|no|seq|순번|번호)$/i, type: 'index' },
    // 금액
    { pattern: /(amount|total|price|금액|가액|합계)/i, type: 'currency' },
    // 수량
    { pattern: /(quantity|qty|수량|개수)/i, type: 'quantity' },
    // 단위
    { pattern: /^(unit|uom|단위)$/i, type: 'unit' },
    // 퍼센트
    { pattern: /(rate|percent|%|비율)/i, type: 'percentage' },
    // 날짜
    { pattern: /(date|일|일자|작성일|발행일)/i, type: 'date' },
    // 코드
    { pattern: /(code|번호|no$|number)/i, type: 'code' },
    // 긴 텍스트 (Description 등)
    { pattern: /(description|desc|품명|상호|명칭|주소|remarks|비고)/i, type: 'longText' },
    // 중간 텍스트
    { pattern: /(name|업태|종목)/i, type: 'mediumText' },
];

/**
 * 컬럼 너비 결정 함수 (계층적 lookup)
 * @param {string} fieldKey - 필드 키
 * @param {string} documentType - 문서 유형 ('shipping' | 'businessRegistration' | 'taxInvoice' | 'vehicleRegistration')
 * @returns {number} 컬럼 너비 (px)
 */
const getOptimalColumnWidth = (fieldKey, documentType = 'shipping') => {
    const keyLower = fieldKey.toLowerCase().replace(/[\s_-]/g, '');

    // 1단계: 문서 유형별 특화 너비 확인
    const docWidths = DOCUMENT_TYPE_WIDTHS[documentType];
    if (docWidths) {
        // 정확한 키 매치
        if (docWidths[keyLower] !== undefined) {
            console.log(`📐 [ColumnWidth] ${fieldKey} = ${docWidths[keyLower]}px (정확)`);
            return docWidths[keyLower];
        }

        // 부분 매칭 (키워드 포함 검사)
        for (const [configKey, width] of Object.entries(docWidths)) {
            if (keyLower.includes(configKey) || configKey.includes(keyLower)) {
                console.log(`📐 [ColumnWidth] ${fieldKey} → ${configKey} = ${width}px (부분)`);
                return width;
            }
        }
    }

    // 2단계: 필드 타입 패턴 매칭 → 기본 너비
    for (const { pattern, type } of FIELD_TYPE_PATTERNS) {
        if (pattern.test(fieldKey)) {
            return BASE_WIDTH[type];
        }
    }

    // 3단계: 기본값 (텍스트 길이 기반)
    return fieldKey.length > 10 ? BASE_WIDTH.mediumText : BASE_WIDTH.shortText;
};

// ============================================================

const BatchAUIGrid = ({
    gridId = 'batch-validation-grid',
    flattenedRows = [],
    unifiedColumns = [],
    selectedVehicleId,
    onRowSelect,
    onFieldClick,
    templates,
    selectedTemplateId,
    showMergedView = false
}) => {
    const gridRef = useRef(null);
    const [undoStack, setUndoStack] = useState([]); // 🔄 Undo 스택
    const selectedColumnsRef = useRef([]); // 🆕 다중 열 선택 추적
    const lastClickedColumnRef = useRef(-1); // 🆕 마지막 클릭된 열

    // 🎯 AUIGrid 컬럼 레이아웃 생성 (동적)
    const columnLayout = useMemo(() => {
        const cols = [];

        // 기본 컬럼: 파일명, 페이지
        cols.push({
            dataField: 'fileName',
            headerText: '파일명',
            width: 100,
            style: 'text-overflow:ellipsis;font-size:11px;',
            headerStyle: 'background:#f8fafc;color:#475569;font-weight:600;font-size:11px;'
        });

        cols.push({
            dataField: 'pageNumber',
            headerText: 'P',
            width: 35,
            style: 'text-align:center;font-size:11px;',
            headerStyle: 'background:#f8fafc;color:#475569;font-weight:600;font-size:11px;'
        });

        cols.push({
            dataField: 'sourcePage',
            headerText: '원본',
            width: 50,
            style: 'text-align:center;color:#94a3b8;font-size:10px;',
            headerStyle: 'background:#f8fafc;color:#475569;font-weight:600;font-size:11px;'
        });

        // 동적 필드 컬럼 (unifiedColumns 기반) - 계층적 너비 시스템 적용
        unifiedColumns.forEach((col) => {
            const keyLower = col.key.toLowerCase();
            const isNumeric = ['amount', 'quantity', 'price', 'weight', 'cbm', 'total', '수량', '단가', '금액', 'netweight', 'net'].some(
                keyword => keyLower.includes(keyword)
            );

            cols.push({
                dataField: col.key,
                headerText: col.displayName?.length > 12 ? col.displayName.substring(0, 12) + '...' : col.displayName,
                width: getOptimalColumnWidth(col.key, 'shipping'), // 기본: 수출입 선적서류
                style: isNumeric ? 'text-align:right;font-size:11px;' : 'text-align:left;font-size:11px;',
                headerStyle: 'background:#f8fafc;color:#475569;font-weight:600;font-size:11px;',
                // 교차검증 렌더러
                renderer: {
                    type: 'TemplateRenderer',
                    callback: (rowIndex, columnIndex, value, headerText, item, dataField) => {
                        if (item.hasDifference && item.hasDifference[dataField]) {
                            return `<span style="color:#f59e0b;font-weight:600;" title="교차검증 불일치">${value || '-'}</span>`;
                        }
                        return value || '-';
                    }
                }
            });
        });

        // 작업 컬럼
        cols.push({
            dataField: '_action',
            headerText: '',
            width: 35,
            style: 'text-align:center;font-size:10px;',
            headerStyle: 'background:#f8fafc;',
            editable: false
        });

        return cols;
    }, [unifiedColumns]);

    // 🎯 AUIGrid 데이터 변환 (flattenedRows → AUIGrid 데이터)
    const gridData = useMemo(() => {
        return flattenedRows.map((row, idx) => {
            const vehicle = row.vehicle;
            const tableItem = row.tableData?.[row.itemIndex] || {};
            const current = vehicle?.current || {};

            // 머지 정보
            const sourcePages = vehicle?.sourcePages || [];
            const sourcePagesStr = sourcePages.length > 0
                ? sourcePages.map(p => `P${p.pageNumber}`).join(',')
                : '';

            // 기본 데이터
            const rowData = {
                _rowId: `${vehicle?.id}-${row.itemIndex}`,
                _vehicleId: vehicle?.id,
                _itemIndex: row.itemIndex,
                fileName: vehicle?.fileName || '',
                pageNumber: vehicle?.pageNumber || 1,
                sourcePage: sourcePagesStr,
                hasDifference: {},
                ...current
            };

            // 테이블 필드 데이터 병합 (🔧 FIX: null도 덮어쓰기 대상에 포함)
            Object.keys(tableItem).forEach(key => {
                if (rowData[key] === undefined || rowData[key] === null || rowData[key] === '') {
                    rowData[key] = tableItem[key];
                }
            });

            // 🔍 [DIAG] 첫 몇 행에 대해 Invoice Number 키 존재 여부 확인
            if (idx < 5) {
                const invoiceVal = rowData['Invoice Number'];
                const currentInvoice = current['Invoice Number'];
                const tableInvoice = tableItem['Invoice Number'];
                console.log(`🔍 [gridData Row ${idx}]`, rowData.fileName?.substring(0, 20),
                    'current:', currentInvoice !== undefined ? `"${currentInvoice}"` : 'MISSING',
                    'tableItem:', tableInvoice !== undefined ? `"${tableInvoice}"` : 'MISSING',
                    'final:', invoiceVal !== undefined ? `"${invoiceVal}"` : 'MISSING',
                    'currentKeys:', Object.keys(current).filter(k => !['pageResults','items','metadata'].includes(k)).slice(0, 8).join(',')
                );
            }

            // 교차검증 차이 확인
            if (vehicle?.flashData && vehicle?.proData) {
                unifiedColumns.forEach(col => {
                    const flashVal = vehicle.flashData[col.key];
                    const proVal = vehicle.proData[col.key];
                    if (flashVal !== undefined && proVal !== undefined && flashVal !== proVal) {
                        rowData.hasDifference[col.key] = true;
                    }
                });
            }

            return rowData;
        });
    }, [flattenedRows, unifiedColumns]);

    // 🎯 Undo 핸들러
    const handleUndo = useCallback(() => {
        setUndoStack(prev => {
            if (prev.length === 0) return prev;
            const lastAction = prev[prev.length - 1];
            const newStack = prev.slice(0, -1);

            const gridSelector = `#${gridId}`;
            if (window.AUIGrid && window.AUIGrid.isCreated(gridSelector)) {
                console.log('↩️ [BatchAUIGrid] Undo 실행:', lastAction.type);

                if (lastAction.type === 'merge_column_left') {
                    // 열 전체 병합 되돌리기
                    lastAction.beforeValues.forEach(({ rowIndex, targetValue, sourceValue }) => {
                        window.AUIGrid.updateRow(gridSelector, {
                            [lastAction.targetField]: targetValue,
                            [lastAction.sourceField]: sourceValue
                        }, rowIndex);
                    });
                } else if (lastAction.type === 'merge_up' || lastAction.type === 'merge_left') {
                    // 셀 병합 되돌리기
                    lastAction.beforeValues.forEach(({ rowIndex, field, value }) => {
                        window.AUIGrid.updateRow(gridSelector, { [field]: value }, rowIndex);
                    });
                } else if (lastAction.type === 'cell_update') {
                    // 셀 편집 되돌리기
                    lastAction.beforeValues.forEach(({ rowIndex, field, value }) => {
                        window.AUIGrid.updateRow(gridSelector, { [field]: value }, rowIndex);
                    });
                }
            }
            return newStack;
        });
    }, [gridId]);

    // 🎯 키보드 이벤트 (Ctrl+Z)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                handleUndo();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo]);

    // 🎯 AUIGrid 초기화
    useEffect(() => {
        if (!window.AUIGrid || !gridRef.current) {
            console.warn('⚠️ [BatchAUIGrid] AUIGrid가 로드되지 않았습니다');
            return;
        }

        const gridSelector = `#${gridId}`;

        // 기존 그리드 제거
        if (window.AUIGrid.isCreated(gridSelector)) {
            window.AUIGrid.destroy(gridSelector);
        }

        // 📐 그리드 속성 - 컴팩트 스타일
        const gridProps = {
            width: '100%',
            height: '100%',
            // 행/헤더 설정 - 컴팩트하게
            headerHeight: 32,
            rowHeight: 28,
            // 스크롤
            hScrollEnable: true,
            vScrollEnable: true,
            // 컬럼 기능
            enableColumnResize: true,
            enableSorting: false, // 헤더 클릭 시 열 전체 선택으로 변경 (정렬 비활성화)
            enableFilter: false,
            enableMovingColumn: true,
            // 선택 - ⭐ multipleCells로 변경 (열 선택 지원)
            selectionMode: 'multipleCells',
            showRowNumColumn: true,
            rowNumHeaderText: 'No',
            rowNumWidth: 32,
            // 고정 컬럼 (파일명, 페이지, 원본)
            fixedColumnCount: 3,
            // 스타일
            softRemoveRowMode: false,
            showStateColumn: false,
            editable: true, // ⭐ 편집 가능하도록 설정
            // 테마 (Google AI Studio 느낌)
            headerBackground: '#f8fafc',
            selectionColor: '#e0f2fe',
            hoverColor: '#f1f5f9',
            // 컬럼 구분선
            showRowBorder: true,
            showColumnBorder: true,
            rowBorderColor: '#e2e8f0',
            columnBorderColor: '#e2e8f0',
            // 🆕 컨텍스트 메뉴 설정 (PageBasedGridPanel과 동기화)
            useContextMenu: true,
            contextMenuItems: [
                { label: "행 추가", callback: "addRow" },
                { label: "행 삭제", callback: "deleteRow" },
                { label: "열 삭제", callback: "deleteColumn" },
                { label: "-" },
                { label: "현재 셀을 위 셀에 병합", callback: "mergeUp" },
                { label: "현재 셀을 왼쪽 셀에 병합", callback: "mergeLeft" },
                { label: "-" },
                { label: "🔗 열 전체를 왼쪽 열에 병합", callback: "mergeColumnLeft" }
            ]
        };

        // 그리드 생성
        window.AUIGrid.create(gridSelector, columnLayout, gridProps);
        window.AUIGrid.setGridData(gridSelector, gridData);

        console.log(`✅ [BatchAUIGrid] 그리드 생성 완료: ${gridData.length}행, ${columnLayout.length}컬럼`);

        // 🎯 이벤트 바인딩

        // 🆕 헤더 클릭 → 열 전체 선택 (Ctrl+Click 토글, Shift+Click 범위)
        window.AUIGrid.bind(gridSelector, 'headerClick', (e) => {
            const columnIndex = e.columnIndex;
            const dataField = e.dataField;
            if (!dataField || dataField === '_rownum') return false;

            const rowCount = window.AUIGrid.getRowCount(gridSelector);
            if (rowCount <= 0) return false;

            const clearColumnHighlight = () => {
                document.querySelectorAll(`#${gridId} .aui-grid-header-renderer.selected-column-header`).forEach(el => {
                    el.classList.remove('selected-column-header');
                });
            };

            const applyColumnHighlight = (columns) => {
                clearColumnHighlight();
                const gridEl = document.getElementById(gridId);
                if (!gridEl) return;
                const headerCells = gridEl.querySelectorAll('.aui-grid-header-renderer');
                columns.forEach(colIdx => {
                    if (headerCells[colIdx]) {
                        headerCells[colIdx].classList.add('selected-column-header');
                    }
                });
            };

            if (e.orgEvent?.shiftKey && lastClickedColumnRef.current >= 0) {
                const start = Math.min(lastClickedColumnRef.current, columnIndex);
                const end = Math.max(lastClickedColumnRef.current, columnIndex);
                selectedColumnsRef.current = [];
                for (let i = start; i <= end; i++) {
                    selectedColumnsRef.current.push(i);
                }
                window.AUIGrid.setSelectionBlock(gridSelector, 0, start, rowCount - 1, end);
                applyColumnHighlight(selectedColumnsRef.current);
            } else if (e.orgEvent?.ctrlKey || e.orgEvent?.metaKey) {
                const idx = selectedColumnsRef.current.indexOf(columnIndex);
                if (idx >= 0) {
                    selectedColumnsRef.current.splice(idx, 1);
                } else {
                    selectedColumnsRef.current.push(columnIndex);
                }
                if (selectedColumnsRef.current.length > 0) {
                    const lastCol = selectedColumnsRef.current[selectedColumnsRef.current.length - 1];
                    window.AUIGrid.setSelectionBlock(gridSelector, 0, lastCol, rowCount - 1, lastCol);
                }
                applyColumnHighlight(selectedColumnsRef.current);
            } else {
                selectedColumnsRef.current = [columnIndex];
                window.AUIGrid.setSelectionBlock(gridSelector, 0, columnIndex, rowCount - 1, columnIndex);
                applyColumnHighlight([columnIndex]);
            }

            lastClickedColumnRef.current = columnIndex;
            return false;
        });

        // 🆕 컨텍스트 메뉴 바인딩 (PageBasedGridPanel과 동기화)
        window.AUIGrid.bind(gridSelector, 'contextMenu', (e) => {
            const menuId = e.item?.callback;
            if (!menuId) return;

            const rowIndex = e.rowIndex;
            const columnIndex = e.columnIndex;
            const dataField = e.dataField;

            switch (menuId) {
                case 'addRow': {
                    const position = window.prompt('행 추가 위치:\n1: 위에 추가\n2: 아래에 추가', '2');
                    if (position === '1') {
                        window.AUIGrid.addRow(gridSelector, {}, "selectionUp");
                    } else if (position === '2') {
                        window.AUIGrid.addRow(gridSelector, {}, "selectionDown");
                    }
                    break;
                }
                case 'mergeUp': {
                    if (rowIndex <= 0) {
                        alert('위로 병합할 수 없습니다. (첫 번째 행)');
                        return;
                    }
                    const allData = window.AUIGrid.getGridData(gridSelector);
                    const currentValue = allData[rowIndex]?.[dataField] || '';
                    const aboveValue = allData[rowIndex - 1]?.[dataField] || '';
                    const mergedValue = aboveValue ? `${aboveValue}, ${currentValue}` : currentValue;

                    setUndoStack(prev => [...prev, {
                        id: Date.now(),
                        type: 'merge_up',
                        beforeValues: [
                            { rowIndex: rowIndex - 1, field: dataField, value: aboveValue },
                            { rowIndex: rowIndex, field: dataField, value: currentValue }
                        ]
                    }]);

                    window.AUIGrid.updateRow(gridSelector, { [dataField]: mergedValue }, rowIndex - 1);
                    window.AUIGrid.updateRow(gridSelector, { [dataField]: '' }, rowIndex);
                    break;
                }
                case 'mergeLeft': {
                    const layout = window.AUIGrid.getColumnLayout(gridSelector);
                    if (columnIndex <= 0) {
                        alert('왼쪽으로 병합할 수 없습니다. (첫 번째 열)');
                        return;
                    }
                    const leftField = layout[columnIndex - 1]?.dataField;
                    if (!leftField) return;

                    const allData = window.AUIGrid.getGridData(gridSelector);
                    const currentValue = allData[rowIndex]?.[dataField] || '';
                    const leftValue = allData[rowIndex]?.[leftField] || '';
                    const mergedValue = leftValue ? `${leftValue}, ${currentValue}` : currentValue;

                    setUndoStack(prev => [...prev, {
                        id: Date.now(),
                        type: 'merge_left',
                        beforeValues: [
                            { rowIndex, field: leftField, value: leftValue },
                            { rowIndex, field: dataField, value: currentValue }
                        ]
                    }]);

                    window.AUIGrid.updateRow(gridSelector, { [leftField]: mergedValue, [dataField]: '' }, rowIndex);
                    break;
                }
                case 'mergeColumnLeft': {
                    if (columnIndex <= 3) {
                        alert('이 열은 병합할 수 없습니다.');
                        return;
                    }
                    const layout = window.AUIGrid.getColumnLayout(gridSelector);
                    const targetField = layout[columnIndex - 1]?.dataField;
                    if (!targetField) return;

                    const allData = window.AUIGrid.getGridData(gridSelector);
                    const beforeValues = [];

                    allData.forEach((row, idx) => {
                        const sourceVal = row[dataField] || '';
                        const targetVal = row[targetField] || '';
                        if (sourceVal) {
                            beforeValues.push({
                                rowIndex: idx,
                                targetValue: targetVal,
                                sourceValue: sourceVal
                            });
                        }
                    });

                    beforeValues.forEach(({ rowIndex: idx, targetValue, sourceValue }) => {
                        const mergedVal = targetValue ? `${targetValue}, ${sourceValue}` : sourceValue;
                        window.AUIGrid.updateRow(gridSelector, {
                            [targetField]: mergedVal,
                            [dataField]: ''
                        }, idx);
                    });

                    if (beforeValues.length > 0) {
                        setUndoStack(prev => [...prev, {
                            id: Date.now(),
                            type: 'merge_column_left',
                            sourceField: dataField,
                            targetField: targetField,
                            beforeValues
                        }]);
                        console.log('✅ [BatchAUIGrid] 열 병합 완료:', dataField, '->', targetField);
                    }
                    break;
                }
                case 'deleteRow': {
                    const selected = window.AUIGrid.getSelectedItems(gridSelector);
                    const indices = Array.from(new Set(selected.map(s => s.rowIndex))).sort((a, b) => b - a);
                    if (indices.length > 0) {
                        if (window.confirm(`${indices.length}개 행을 삭제하시겠습니까?`)) {
                            indices.forEach(idx => window.AUIGrid.removeRow(gridSelector, idx));
                        }
                    } else if (rowIndex >= 0) {
                        if (window.confirm(`${rowIndex + 1}번 행을 삭제하시겠습니까?`)) {
                            window.AUIGrid.removeRow(gridSelector, rowIndex);
                        }
                    }
                    break;
                }
                case 'deleteColumn': {
                    if (columnIndex <= 2) {
                        alert('기본 컬럼은 삭제할 수 없습니다.');
                        return;
                    }
                    if (window.confirm(`'${dataField}' 열을 삭제하시겠습니까?`)) {
                        window.AUIGrid.removeColumn(gridSelector, columnIndex);
                    }
                    break;
                }
                default: break;
            }
        });

        // 행 선택
        window.AUIGrid.bind(gridSelector, 'selectionChange', (e) => {
            if (e.items && e.items.length > 0) {
                const item = e.items[0].item;
                const vehicleId = item._vehicleId;
                if (vehicleId) onRowSelect?.(vehicleId);
            }
        });

        // 셀 클릭 → bbox 하이라이트 + 행 선택
        window.AUIGrid.bind(gridSelector, 'cellClick', (e) => {
            const fieldKey = e.dataField;
            const value = e.value;
            const item = e.item;

            if (item && item._vehicleId) {
                onRowSelect?.(item._vehicleId);
            }

            if (fieldKey && fieldKey !== '_action' && !['fileName', 'pageNumber', 'sourcePage'].includes(fieldKey)) {
                onFieldClick?.(fieldKey, value);
            }
        });

        // 🆕 셀 편집 완료 핸들러 (Undo 지원용)
        window.AUIGrid.bind(gridSelector, 'cellEditBegin', (e) => {
            // 편집 전 값 저장 로직은 필요 시 추가
        });

        window.AUIGrid.bind(gridSelector, 'cellEditEnd', (e) => {
            if (e.oldValue !== e.value) {
                setUndoStack(prev => [...prev, {
                    id: Date.now(),
                    type: 'cell_update',
                    beforeValues: [{
                        rowIndex: e.rowIndex,
                        field: e.dataField,
                        value: e.oldValue
                    }]
                }]);
            }
        });

        // 🆕 헤더 더블클릭 → 컬럼 자동 너비 조절
        window.AUIGrid.bind(gridSelector, 'headerDoubleClick', (e) => {
            const columnIndex = e.columnIndex;
            try {
                window.AUIGrid.autoFitColumnWidth(gridSelector, columnIndex);
            } catch (err) {
                console.warn('컬럼 자동 너비 조절 실패:', err);
            }
        });

        // 클린업
        return () => {
            if (window.AUIGrid && window.AUIGrid.isCreated(gridSelector)) {
                try {
                    window.AUIGrid.destroy(gridSelector);
                } catch (e) {
                    console.warn('그리드 정리 오류:', e);
                }
            }
        };
    }, [gridId, columnLayout, gridData, onRowSelect, onFieldClick, handleUndo]);

    // 🎯 선택된 행 하이라이트
    useEffect(() => {
        if (!window.AUIGrid || !selectedVehicleId) return;

        const gridSelector = `#${gridId}`;
        if (!window.AUIGrid.isCreated(gridSelector)) return;

        // 선택된 vehicleId에 해당하는 행 찾기
        const allData = window.AUIGrid.getGridData(gridSelector);
        const rowIndex = allData.findIndex(item => item._vehicleId === selectedVehicleId);

        if (rowIndex >= 0) {
            // selectionMode가 multipleCells일 때는 전체 행 선택이 다르게 동작할 수 있음
            // 여기서는 첫 번째 셀 선택으로 대체하거나 setSelectionBlock 사용
            window.AUIGrid.setSelectionByIndex(gridSelector, rowIndex, 0);
        }
    }, [gridId, selectedVehicleId]);

    // 🎯 엑셀 내보내기
    const handleExcelExport = useCallback(() => {
        const gridSelector = `#${gridId}`;
        if (window.AUIGrid && window.AUIGrid.isCreated(gridSelector)) {
            window.AUIGrid.exportToXlsx(gridSelector, {
                fileName: `검증결과_${new Date().toISOString().slice(0, 10)}.xlsx`,
                sheetName: '검증결과'
            });
        }
    }, [gridId]);

    // 외부에서 엑셀 내보내기 호출 가능하도록 window에 등록
    useEffect(() => {
        window.BatchAUIGridExport = handleExcelExport;
        return () => {
            delete window.BatchAUIGridExport;
        };
    }, [handleExcelExport]);

    return (
        <div
            className="w-full h-full"
            style={{ minHeight: '300px' }}
        >
            <div
                id={gridId}
                ref={gridRef}
                style={{ width: '100%', height: '100%' }}
            />
        </div>
    );
};



export default BatchAUIGrid;
